import React from 'react';
import SectionShell from '../SectionShell';
import Reveal from '../Reveal';
import RankName from '../RankName';
import { useT } from '../../i18n/LanguageContext';

/**
 * Folie 30 — Infinity Bonus.
 * Refactored to match the visual language of Worldpools / Rang Bonus / Karriere:
 * ascending staircase of 10 rank cards with the original PPTX coins, rank name,
 * and per-rank Infinity % (Sapphire 3% → Black Diamond 24%). Compact header row
 * with title + body + example calc card.
 */
export default function SectionInfinityBonus() {
  const { t } = useT();
  const m = t.infinity;

  return (
    <SectionShell id="infinity-bonus" className="">
      {/* Header row: [title + sub on left] | [example on right] */}
      <div className="grid grid-cols-12 gap-y-8 md:gap-x-10 items-start mb-10">
        <div className="col-span-12 md:col-span-8">
          <Reveal>
            <div className="overline mb-4">{m.overline}</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-white max-w-[14ch] mb-6">
              {m.title}
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <p className="font-ui text-3xl md:text-4xl lg:text-[2.6rem] text-white/85 leading-snug max-w-[68ch]">
              {m.sub}
            </p>
          </Reveal>
        </div>
        <div className="col-span-12 md:col-span-4">
          <Reveal delay={200}>
            <div className="infinity-example" data-testid="infinity-example">
              <div className="overline mb-3 text-[#f2d979]/80">{m.exampleTitle}</div>
              <div className="space-y-1.5 mb-3">
                {m.exampleRows.map((r, i) => (
                  <div key={i} className="flex items-center justify-between font-ui text-xs md:text-sm text-white/70">
                    <span>{r.label}</span>
                    <span className="font-display text-base text-white">{r.value}</span>
                  </div>
                ))}
              </div>
              <div className="h-px bg-gradient-to-r from-transparent via-[#f2d979]/40 to-transparent my-2" />
              <div className="flex items-center justify-between">
                <span className="font-ui text-xs md:text-sm text-[#f2d979]/85">{m.exampleResult.label}</span>
                <span className="font-display text-2xl md:text-3xl text-[#f2d979]">{m.exampleResult.value}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>

      {/* 10 ascending rank cards */}
      <Reveal delay={240}>
        <div className="ib-matrix-scroll">
          <div className="ib-matrix" data-testid="infinity-matrix">
            {m.ranks.map((r, idx) => (
              <div
                key={r.name}
                className="wp-rank-card ib-rank-card"
                data-testid={`infinity-rank-${idx}`}
                style={{ '--step': idx }}
              >
                <div className="wp-coin">
                  <img
                    src={`/source-pptx/${r.coin}`}
                    alt={`${r.name} Rang`}
                    loading="lazy"
                    draggable={false}
                    className="block w-full h-full object-contain select-none"
                    style={{ filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.6))' }}
                  />
                </div>
                <div className="wp-rank-name font-display"><RankName name={r.name} /></div>
                <div className="ib-value font-display">{r.value}</div>
              </div>
            ))}
          </div>
        </div>
      </Reveal>

      <Reveal delay={620}>
        <p className="font-ui text-xs text-white/45 italic text-center mt-8 max-w-[80ch] mx-auto">
          {m.totalNote}
        </p>
      </Reveal>
    </SectionShell>
  );
}
